import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type * as EchartsCore from 'echarts/core';
import type { EChartsType } from 'echarts/core';
import type { EChartsOption } from 'echarts';

type EChartsCoreModule = typeof EchartsCore;

const OPTION_UPDATE = { notMerge: true, lazyUpdate: true } as const;
/** Start loading a bit before the chart enters the viewport so scroll-in is not empty. */
const CHART_ROOT_MARGIN = '400px';

let echartsModulePromise: Promise<EChartsCoreModule> | null = null;

async function loadECharts(): Promise<EChartsCoreModule> {
  if (!echartsModulePromise) {
    echartsModulePromise = (async () => {
      const [core, charts, components, renderers, features] = await Promise.all([
        import('echarts/core'),
        import('echarts/charts'),
        import('echarts/components'),
        import('echarts/renderers'),
        import('echarts/features'),
      ]);
      core.use([
        charts.LineChart,
        charts.BarChart,
        charts.PieChart,
        charts.FunnelChart,
        charts.ScatterChart,
        charts.CandlestickChart,
        components.GridComponent,
        components.LegendComponent,
        components.TooltipComponent,
        renderers.CanvasRenderer,
        features.LegacyGridContainLabel,
      ]);
      return core;
    })();
  }
  return echartsModulePromise;
}

export function resetEChartsForTests(): void {
  echartsModulePromise = null;
}

function optionKey(option: EChartsOption) {
  return JSON.stringify(option);
}

function applyOption(instance: EChartsType, option: EChartsOption, lastKey: { current: string }) {
  const key = optionKey(option);
  if (lastKey.current === key) return;
  lastKey.current = key;
  instance.setOption(option, OPTION_UPDATE);
}

/** One echarts instance: init when near the viewport, skip duplicate options, resize only while visible. */
export function useEChart(containerRef: RefObject<HTMLElement | null>, option: EChartsOption) {
  const chartRef = useRef<EChartsType | null>(null);
  const optionRef = useRef(option);
  const optionKeyRef = useRef('');
  const visibleRef = useRef(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    optionRef.current = option;
    if (chartRef.current) applyOption(chartRef.current, option, optionKeyRef);
  }, [option]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    let disposed = false;
    let resizeObserver: ResizeObserver | null = null;
    let intersectionObserver: IntersectionObserver | null = null;

    const attachResize = (instance: EChartsType, node: HTMLElement) => {
      if (typeof ResizeObserver === 'undefined') return;
      resizeObserver = new ResizeObserver(() => {
        if (visibleRef.current) instance.resize();
      });
      resizeObserver.observe(node);
    };

    const ensureInit = () => {
      if (disposed || chartRef.current || !visibleRef.current) return;
      void loadECharts()
        .then((echarts) => {
          const node = containerRef.current;
          if (disposed || chartRef.current || !node || !visibleRef.current) return;
          const instance = echarts.init(node);
          chartRef.current = instance;
          applyOption(instance, optionRef.current, optionKeyRef);
          attachResize(instance, node);
        })
        .catch(() => {
          chartRef.current = null;
        });
    };

    if (typeof IntersectionObserver === 'undefined') {
      visibleRef.current = true;
      ensureInit();
    } else {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((entry) => entry.isIntersecting);
          visibleRef.current = visible;
          if (!visible) return;
          if (chartRef.current) chartRef.current.resize();
          else ensureInit();
        },
        { rootMargin: CHART_ROOT_MARGIN },
      );
      intersectionObserver.observe(element);
    }

    return () => {
      disposed = true;
      intersectionObserver?.disconnect();
      resizeObserver?.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [containerRef]);
}
